import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./SubscriptionManagement.scss";
import searchIconIMG from "../../assets/images/megnificant-glass.png";
import CommonTable from "../../components/Table/CommonTable";
import EditSubscription from "./EditSubscription";
import SubscriptionDetail from "./SubscriptionDetail";
import useApi from "../../hook/useAPI";
import { SUBSCRIPTION } from "../../utils/const";
import {
  formatPlanDescription,
  formatPlanPriceSummary,
  getPlanCatalogType,
  getPlanCatalogTypeLabel,
} from "../../utils/planCatalogDisplay";
import edit from "../../assets/images/edit.svg";
import eye from "../../assets/images/eye.svg";
import { debounce } from "lodash";

const PlanTypeBadge = ({ plan }) => {
  const type = getPlanCatalogType(plan);
  const label = getPlanCatalogTypeLabel(plan);
  return <span className={`plan-catalog-type-badge ${type}`}>{label}</span>;
};

const SubscriptionManagement = () => {
  const { getData } = useApi();
  const [searchQuery, setSearchQuery] = useState("");
  const [catalogPlans, setCatalogPlans] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalMatching, setTotalMatching] = useState(0);
  const [showView, setShowView] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState();

  const columnsOptions = [
    { header: "Plan name", key: "planName" },
    { header: "Type", key: "planType" },
    { header: "Description", key: "description" },
    { header: "Price", key: "priceSummary" },
    { header: "Action", key: "action" },
  ];

  const handleEditPrice = useCallback((id) => {
    setSubscriptionId(id);
    setShowEdit(true);
  }, []);

  const handleUserDetails = useCallback((item) => {
    setSubscriptionData(item);
    setShowView(true);
  }, []);

  const ActionColumn = useCallback((item) => (
    <div className="action-control-btn subscription-action-btns">
      <button
        type="button"
        className="action-btn"
        onClick={() => handleUserDetails(item)}
        aria-label={`View ${item.planName}`}
      >
        <img src={eye} alt="" />
      </button>
      <button
        type="button"
        className="action-btn"
        onClick={() => handleEditPrice(item._id)}
        aria-label={`Edit ${item.planName}`}
      >
        <img src={edit} alt="" />
      </button>
    </div>
  ), [handleEditPrice, handleUserDetails]);

  const mapPlanToRow = useCallback((item) => ({
    ...item,
    planType: <PlanTypeBadge plan={item} />,
    description: formatPlanDescription(item.description),
    priceSummary: formatPlanPriceSummary(item),
    action: ActionColumn(item),
  }), [ActionColumn]);

  const loadPlans = useCallback(async (search = "") => {
    try {
      const response = await getData(
        `${SUBSCRIPTION.GET_PRICE}?search=${encodeURIComponent(search || "")}`,
      );
      const plans = Array.isArray(response?.data) ? response.data : [];
      setCatalogPlans(plans);
    } catch (error) {
      console.error("Error loading plan catalog:", error);
      setCatalogPlans([]);
    }
  }, [getData]);

  const debouncedSearch = useMemo(
    () => debounce((search) => {
      loadPlans(search);
    }, 400),
    [loadPlans],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    if (!showEdit) {
      loadPlans(searchQuery);
    }
    // Reload catalog when the edit modal closes; search uses debouncedSearch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEdit]);

  useEffect(() => {
    const total = catalogPlans.length;
    const pages = total > 0 ? Math.ceil(total / itemsPerPage) : 0;
    const safePage = pages > 0 ? Math.min(currentPage, pages) : 1;

    if (safePage !== currentPage && pages > 0) {
      setCurrentPage(safePage);
      return;
    }

    const start = (safePage - 1) * itemsPerPage;
    const pagePlans = catalogPlans.slice(start, start + itemsPerPage);
    setTableRows(pagePlans.map(mapPlanToRow));
    setTotalMatching(total);
    setTotalPages(pages);
  }, [catalogPlans, currentPage, itemsPerPage, mapPlanToRow]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    debouncedSearch(value);
  };

  const handleClose = () => {
    setShowEdit(false);
    setShowView(false);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="dashboard subscription-mgmt-page">
      <div className="row">
        <div className="col-12">
          <div className="card card-table-wrap">
            <div className="card-body p-0">
              <div className="home-table-chart-box subscription-mgmt-table-wrap">
                <div className="top_bar subscription-mgmt-top-bar">
                  <div className="subscription-mgmt-top-bar__left">
                    <div className="card_title">Plan catalog</div>
                    <p className="subscription-mgmt-subtitle">
                      Manage subscription tiers, add-ons, and Stripe price IDs.
                    </p>
                  </div>
                  <div className="top_bar_right subscription-mgmt-toolbar">
                    <div className="input-group subscription-mgmt-search">
                      <span className="input-group-text" id="subscription-search-addon">
                        <img src={searchIconIMG} className="img-fluid" alt="" />
                      </span>
                      <input
                        type="search"
                        className="form-control"
                        placeholder="Search plan name or description"
                        aria-label="Search plans"
                        aria-describedby="subscription-search-addon"
                        value={searchQuery}
                        onChange={handleSearchChange}
                      />
                    </div>
                    {totalMatching > 0 && (
                      <span className="subscription-mgmt-result-count">
                        {totalMatching} {totalMatching === 1 ? "plan" : "plans"}
                      </span>
                    )}
                  </div>
                </div>

                <CommonTable
                  columns={columnsOptions}
                  rows={tableRows}
                  itemsPerPage={itemsPerPage}
                  totalPages={totalPages}
                  currentPage={currentPage}
                  totalCount={totalMatching}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  onPageChange={handlePageChange}
                  isPagination
                  pageName="subscription"
                  showHeaderWhenEmpty
                  hidePaginationWhenEmpty
                  emptyState={{
                    title: searchQuery.trim() ? "No plans match your search" : "No plans in catalog",
                    message: searchQuery.trim()
                      ? "Try a different search term."
                      : "Seed plans on the backend to populate this table.",
                  }}
                />

                <EditSubscription
                  show={showEdit}
                  handleClose={handleClose}
                  subscriptionId={subscriptionId}
                  getPlans={() => loadPlans(searchQuery)}
                />
                <SubscriptionDetail
                  show={showView}
                  handleClose={handleClose}
                  subscriptionData={subscriptionData}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManagement;
